import { Group, Loader, Paper, Stack, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCirclePlus,
  IconMedicalCross,
  IconTrash,
} from "@tabler/icons-react";

import DisabledReason from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import {
  ProceduresTabConditionData,
  ProceduresTabProcedureData,
} from "@/encounters/types/procedures";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";

interface ProcedureRowProps {
  procedure: ProceduresTabProcedureData;
  /** Takes the provider to the Procedures tab. Absent while the encounter is locked. */
  onEdit?: () => void;
}

/** One procedure recorded under the diagnosis: its CPT code, what it is, and the way in to change it. */
function ProcedureRow({ procedure, onEdit }: ProcedureRowProps) {
  return (
    <Group
      px="md"
      py="sm"
      gap="md"
      bg="gray.0"
      wrap="nowrap"
      align="flex-start"
      justify="space-between"
      data-testid="encounter-diagnosis-procedure-row"
      style={{ borderRadius: "var(--mantine-radius-sm)" }}
    >
      <Group gap="xs" wrap="nowrap">
        <TextWrapper fw={600} c="gray.7">
          CPT
        </TextWrapper>
        <TextWrapper fw={600} c="dark.9">
          {procedure.code}
        </TextWrapper>
      </Group>
      <Stack gap={0} align="flex-end" style={{ minWidth: 0 }}>
        <TextWrapper c="dark.9" ta="right">
          {procedure.display}
        </TextWrapper>
        {onEdit && (
          <PermissionGate requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}>
            <UnstyledButton
              onClick={onEdit}
              data-testid="encounter-diagnosis-procedure-edit-button"
            >
              <TextWrapper fw={600} c="burgundy.9" td="underline">
                Edit
              </TextWrapper>
            </UnstyledButton>
          </PermissionGate>
        )}
      </Stack>
    </Group>
  );
}

/**
 * That the diagnosis is on the client's problem list, to a provider who is not offered the way to
 * change it.
 *
 * Membership is state, not an action, so it survives the gates the actions are behind: a locked
 * encounter and a read-only practitioner both still see which diagnoses are on the list, the way the
 * diagnoses list this replaces shows it. Where the actions are offered, ProblemListButton carries
 * the same state, so the two never sit beside each other.
 *
 * gray/6 is the design system's icon-stroke step. The label is a step darker than the design's own
 * gray/6, which fails AA as text on white — the same dim the card already uses for its labels.
 */
const ProblemListIndicator = () => (
  <Group gap="xs" wrap="nowrap" data-testid="encounter-diagnosis-in-problem-list">
    <IconCheck size={20} color="var(--mantine-color-gray-6)" />
    <TextWrapper fw={600} c="gray.7">
      In Problem List
    </TextWrapper>
  </Group>
);

/** Why the control is offered but cannot be used yet. */
const UNSAVED_DIAGNOSIS_LABEL =
  "Save the encounter before putting this diagnosis on the client's problem list";

type ProblemListButtonProps = {
  isInProblemList: boolean;
  /** False while the diagnosis is only staged: there is no resource to put on the list yet. */
  isPersisted: boolean;
  isPending: boolean;
  onToggle: () => void;
};

/**
 * Whether the diagnosis is on the client's problem list, and the way to change it.
 *
 * One control with two readings rather than a button and a badge: the state and the action on it are
 * the same thing, so a provider who can see that a diagnosis is on the list can take it off from
 * there. A diagnosis the encounter has not written yet has nothing to put on the list, so the control
 * is disabled and says why instead of failing on the click.
 *
 * Where the provider cannot act at all, ProblemListIndicator reads out the same state instead, so
 * only one of the two is ever on screen.
 */
function ProblemListButton({
  isInProblemList,
  isPersisted,
  isPending,
  onToggle,
}: ProblemListButtonProps) {
  const isDisabled = !isPersisted || isPending;
  // gray/6 is the design system's icon-stroke step; its text tier is a step darker, because gray/6
  // fails AA on white.
  const color = isInProblemList ? "gray.7" : isPersisted ? "burgundy.9" : "gray.5";

  const button = (
    <UnstyledButton
      onClick={onToggle}
      disabled={isDisabled}
      style={{ cursor: isDisabled ? "not-allowed" : undefined }}
      data-testid={
        isInProblemList
          ? "encounter-diagnosis-in-problem-list-button"
          : "encounter-diagnosis-add-to-problem-list-button"
      }
    >
      <Group gap="xs" wrap="nowrap">
        {isPending ? (
          <Loader size={16} color="gray.6" />
        ) : isInProblemList ? (
          <IconCheck size={20} color="var(--mantine-color-gray-6)" />
        ) : (
          <IconCirclePlus
            size={20}
            color={isPersisted ? "var(--mantine-color-burgundy-9)" : "var(--mantine-color-gray-5)"}
          />
        )}
        <TextWrapper fw={600} c={color}>
          {isInProblemList ? "In Problem List" : "Add to Problem List"}
        </TextWrapper>
      </Group>
    </UnstyledButton>
  );

  return (
    <DisabledReason reason={isPersisted ? undefined : UNSAVED_DIAGNOSIS_LABEL}>
      {button}
    </DisabledReason>
  );
}

interface EncounterDiagnosisCardProps {
  condition: ProceduresTabConditionData;
  /** The procedures recorded under this diagnosis. */
  procedures: ProceduresTabProcedureData[];
  /**
   * Whether the encounter still accepts changes. A locked encounter shows the diagnosis and offers
   * nothing on it, the same way the existing diagnoses list hides Edit and Delete. What is already
   * on the client's problem list still says so — that is the record, not something to act on.
   */
  canEditDiagnoses: boolean;
  /**
   * Opens the confirmation for removing this diagnosis. Absent where the shell would never write
   * what that confirmation stages, so a removal cannot be confirmed and then silently dropped.
   */
  onDelete?: () => void;
  /** Puts the diagnosis on the client's problem list, or takes it off if it is already on it. */
  onToggleProblemList: () => void;
  /** True while the problem-list write for this diagnosis is in flight. */
  isProblemListPending: boolean;
  /**
   * Take the provider to the Procedures tab, where the encounter's procedures are edited.
   *
   * Both are absent in the shells that have no Procedures tab — a labor encounter's Dx tab and an
   * amendment's — so the controls that would have nowhere to go are not rendered there.
   */
  onEdit?: () => void;
  onAddProcedure?: () => void;
}

/**
 * One diagnosis on the encounter, with the procedures recorded under it.
 *
 * The card decides nothing about what its actions do: Edit and Add Procedure are handed the way to
 * the Procedures tab, Delete the confirmation to open, and Add to Problem List the write to make, all
 * closed over the resources this card was built from — so the card never sees a FHIR resource.
 */
function EncounterDiagnosisCard({
  condition,
  procedures,
  canEditDiagnoses,
  onDelete,
  onToggleProblemList,
  isProblemListPending,
  onEdit,
  onAddProcedure,
}: EncounterDiagnosisCardProps) {
  const [isExpanded, { toggle }] = useDisclosure(true);

  return (
    <Paper
      p="lg"
      bg="white"
      radius="md"
      data-testid="encounter-diagnosis-card"
      bd="1px solid var(--mantine-color-cream-4)"
    >
      <Group gap="md" wrap="nowrap" align="center" justify="space-between">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconMedicalCross
            size={24}
            style={{ flexShrink: 0 }}
            color="var(--mantine-color-gray-7)"
          />
          <TextWrapper fw={600} c="dark.9">
            {condition.code} - {condition.display}
          </TextWrapper>
        </Group>
        <Group gap="md" wrap="nowrap">
          {canEditDiagnoses ? (
            <PermissionGate
              requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}
              fallback={condition.isInProblemList ? <ProblemListIndicator /> : null}
            >
              <Group gap="md" wrap="nowrap">
                <ProblemListButton
                  onToggle={onToggleProblemList}
                  isPending={isProblemListPending}
                  isPersisted={condition.isPersisted}
                  isInProblemList={condition.isInProblemList}
                />
                {onDelete && (
                  <UnstyledButton
                    onClick={onDelete}
                    data-testid="encounter-diagnosis-delete-button"
                  >
                    <Group gap="xs" wrap="nowrap">
                      <IconTrash size={24} color="var(--mantine-color-red-8)" />
                      <TextWrapper fw={600} c="red.8">
                        Delete
                      </TextWrapper>
                    </Group>
                  </UnstyledButton>
                )}
              </Group>
            </PermissionGate>
          ) : (
            condition.isInProblemList && <ProblemListIndicator />
          )}
          <UnstyledButton
            onClick={toggle}
            data-testid="encounter-diagnosis-toggle-button"
            aria-label={isExpanded ? "Collapse diagnosis" : "Expand diagnosis"}
          >
            {isExpanded ? <IconChevronUp size={24} /> : <IconChevronDown size={24} />}
          </UnstyledButton>
        </Group>
      </Group>

      {isExpanded && (
        <Stack mt="md" gap="xs">
          <TextWrapper fw={600} c="gray.7">
            Procedure Codes
          </TextWrapper>
          {procedures.map((procedure) => (
            <ProcedureRow
              procedure={procedure}
              key={procedure.id ?? procedure.code}
              onEdit={canEditDiagnoses ? onEdit : undefined}
            />
          ))}
          {canEditDiagnoses && onAddProcedure && (
            <PermissionGate requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}>
              <UnstyledButton
                h={40}
                bg="gray.0"
                onClick={onAddProcedure}
                data-testid="encounter-diagnosis-add-procedure-button"
                style={{
                  borderRadius: "var(--mantine-radius-sm)",
                  border: "1px dashed var(--mantine-color-gray-6)",
                }}
              >
                <Group h="100%" align="center" justify="center">
                  <TextWrapper fw={600} c="burgundy.9" td="underline">
                    + Add Procedure
                  </TextWrapper>
                </Group>
              </UnstyledButton>
            </PermissionGate>
          )}
        </Stack>
      )}
    </Paper>
  );
}

export default EncounterDiagnosisCard;
